import type { AccountPlan } from 'types/business2020'
import type { ProductData } from 'types/summary'

import api from '@disco/api/server'

import {
  ChargebeeBillingProviderComponentsTypes,
  DISCOVERY_SUITE,
  STORAGE,
  USER,
  WATERMARKING,
  ADDON_DISCOVERY_SUITE,
  ADDON_WATERMARKING,
  ADDON_STORAGE,
  ADDON_SEATS,
} from '@disco/constants/billing-provider-components'
import {
  ChargebeeAddon,
  ChargebeeAddOnDetailed,
  // TODO: fix /types/types/ in import paths
} from '@disco/types/types/subscription'
import { UNLIMITED_WATERMARKING_QUOTA } from './getWatermarkingQuota'
import { getEnvName } from '@disco/constants/environment'
import type { User } from 'types/user'

type BillingProviderComponentsState = any

export function chargebeePlanPriceLookup(chargebeePlans, handle, period) {
  if (chargebeePlans) {
    const periodMapped = { monthly: 'month', annual: 'year', yearly: 'year' }[
      period
    ]
    if (!periodMapped) throw new Error('cannot map ' + period)
    const match = chargebeePlans.find(
      p => p.plan_id === handle && p.period_unit === periodMapped,
    )
    if (!match) throw new Error(`cannot find plan ${handle} ${period}`)
    return match
  }
}

/**
 * Retrieves a summary of subscription costs based on selected components and quantities.
 *
 * Makes an API call to get pricing information for the selected plan and components.
 * If payment details are provided, it creates a full subscription summary.
 * Otherwise, it calculates pricing based on the component quantities locally.
 *
 * @param options - Configuration options object
 * @param options.billingProviderComponents - Billing provider components state
 * @param options.couponCode - Optional coupon code
 * @param options.discoverySuiteQuantity - Number of discovery suite units
 * @param options.watermarkingQuantity - Number of watermarking units
 * @param options.tracksQuantity - Total tracks quantity
 * @param options.usersQuantity - Total users quantity
 * @param options.setProductData - Function to set the updated product data
 * @param options.period - Billing period ('annual' or 'monthly')
 * @param options.plan - Selected account plan
 * @param options.productData - Current product data
 * @param options.paymentDetails - Optional payment details for checkout
 * @returns Promise resolving to billing manifest or summary object with line items and total price
 *
 * @example
 * ```typescript
 * // Get pricing summary without payment details
 * const pricingSummary = await getSummary({
 *   billingProviderComponents,
 *   couponCode: 'DISCOUNT10',
 *   discoverySuiteQuantity: 3000,
 *   watermarkingQuantity: 50,
 *   tracksQuantity: 12000,
 *   usersQuantity: 8,
 *   setProductData: updateProductData,
 *   period: 'monthly',
 *   plan: businessPlan,
 *   productData: currentProductData,
 *   paymentDetails: null
 * });
 * // Returns { line_items: [...], total_in_cents: 29900 }
 *
 * // Get summary with payment details for checkout
 * const checkoutSummary = await getSummary({
 *   // ...same parameters as above
 *   paymentDetails: {
 *     credit_card_attributes: { ... },
 *     reference: 'order-123',
 *     customer_attributes: { ... }
 *   }
 * });
 * // Returns current_billing_manifest from Chargebee API
 * ```
 */
export const getSummary = async ({
  billingProviderComponents,
  couponCode,
  discoverySuiteQuantity,
  watermarkingQuantity,
  tracksQuantity,
  usersQuantity,
  setProductData,
  period,
  plan,
  productData,
  paymentDetails,
}: {
  billingProviderComponents: BillingProviderComponentsState
  discoverySuiteQuantity: number
  watermarkingQuantity: number
  tracksQuantity: number
  usersQuantity: number
  setProductData: any
  productData: ProductData
  plan: AccountPlan
  period: 'annual' | 'monthly'
  couponCode: string | null | undefined
  paymentDetails:
    | {
        credit_card_attributes: any
        reference: string
        customer_attributes: any
      }
    | null
    | undefined
}): Promise<any> => {
  const chargebeePlan = chargebeePlanPriceLookup(
    billingProviderComponents.chargebeePlans,
    plan.handle,
    period,
  )
  const allAddons = await api.chargebeeListAddons(chargebeePlan.id)
  const signupRequest = {
    plan_id: chargebeePlan.id,
    addons: [
      getChargebeeAddon({
        type: ADDON_DISCOVERY_SUITE,
        allocated_quantity: discoverySuiteQuantity,
        allAddons,
      }),
      getChargebeeAddon({
        type: ADDON_WATERMARKING,
        allocated_quantity: watermarkingQuantity,
        allAddons,
      }),
      getChargebeeAddon({
        type: ADDON_STORAGE,
        allocated_quantity: tracksQuantity - (plan.files.defaultCount || 0),
        allAddons,
      }),
      getChargebeeAddon({
        type: ADDON_SEATS,
        allocated_quantity: usersQuantity - (plan.users.defaultCount || 0),
        allAddons,
      }),
    ].flat(),
    coupon: couponCode,
  }
  let estimate
  try {
    estimate = await api.chargebeeEstimateSignup(signupRequest)
  } catch (err) {
    if (err.body?.error_code) {
      console.log('API error on chargebeeEstimateSignup', err.body)
      const { error_code, error_msg } = err.body
      return {
        error: {
          error_code,
          error_msg,
        },
        line_items: [],
        total: null,
      }
    } else {
      throw err
    }
  }

  const SIGNUP_DISCOVERY_SUITE_HANDLE = 'tagging' // TODO: change 'tagging' to ADDON_DISCOVERY_SUITE
  const signupLineItems = estimate.line_items
    .map(li => {
      if (li.type === 'plan') {
        return {
          product_handle: 'plan',
          amount_in_cents: li.amount,
        }
      }
      if (li.type === 'addon') {
        return {
          // TODO: rework mismatching line_items formats
          component_handle: {
            [ADDON_DISCOVERY_SUITE]: SIGNUP_DISCOVERY_SUITE_HANDLE,
            [ADDON_WATERMARKING]: WATERMARKING,
            [ADDON_STORAGE]: STORAGE,
            [ADDON_SEATS]: USER,
          }[li.entitlement_id],
          amount_in_cents: li.amount,
        }
      }
    })
    .filter(Boolean)

  let total_discount_in_cents = 0
  estimate.coupons.forEach(coupon => {
    signupLineItems.push({ kind: 'coupon', coupon: coupon.coupon_code })
    total_discount_in_cents += coupon.amount
  })
  return {
    line_items: signupLineItems,
    total_in_cents: estimate.total,
    subtotal_in_cents: estimate.sub_total,
    total_discount_in_cents,
    signupRequest, // construct request once and use in both estimate_signup and perform_signup
  }
}

/*
Normalises the deprecated snake_case component type ids (eg "discovery_suite")
to the Chargebee entitlement ids (eg "discovery-suite") used by BE endpoints.

So if "discovery_suite" is passed in, return "discovery-suite".
*/
const legacyAddonTypeAdapter = (
  type:
    | string
    | typeof ADDON_DISCOVERY_SUITE
    | typeof ADDON_WATERMARKING
    | typeof ADDON_STORAGE
    | typeof ADDON_SEATS,
): string => {
  const typeMap: Record<string, ChargebeeBillingProviderComponentsTypes> = {
    [DISCOVERY_SUITE]: ADDON_DISCOVERY_SUITE,
    // [WATERMARKING]: ADDON_WATERMARKING - value is same, no need to remap
    [STORAGE]: ADDON_STORAGE,
    [USER]: ADDON_SEATS,
  }
  const legacyType = typeMap[type]
  if (legacyType) {
    // TODO: replace with `throw`, then later remove entirely
    console.warn('legacyAddonTypeAdapter: using deprecated type')
  }
  return legacyType || type
}

/*
Given an quantity of a type (eg 500 for DISCOVERY_SUITE)
-> return the ChargebeeAddon type (eg {id: 'Discovery-Suite-250-tracks', quantity: 2})

ie 500 tracks = quantity 2 of Discovery-Suite-250-tracks

This ChargebeeAddon is the structure used in BE endpoints to estimate and allocate addons.

*/
const UNLIMITED_CHARGEBEE_ENTITLEMENT_VALUE = 'Unlimited'
export const getChargebeeAddon = ({
  type,
  allocated_quantity,
  allAddons, // eg addons just for discovery-suite or watermarking
}: {
  type:
    | string
    | typeof ADDON_DISCOVERY_SUITE
    | typeof ADDON_WATERMARKING
    | typeof ADDON_STORAGE
    | typeof ADDON_SEATS
  allocated_quantity: number
  allAddons: Record<string, ChargebeeAddOnDetailed[]>
}): ChargebeeAddon[] => {
  const chargebeeType = legacyAddonTypeAdapter(type)
  let eligibleAddons = allAddons[chargebeeType]
  if (!eligibleAddons) return [] // for a case like Artist Plan that cannot purchase Tracks
  eligibleAddons = eligibleAddons.map(a => ({
    ...a, // clone as eligibleAddons will be mutated below
    // @ts-ignore ChargebeeAddOnDetailed should have `quantity`
    quantity: 0,
  }))
  let selectedAddon
  if (type === WATERMARKING) {
    if (allocated_quantity >= UNLIMITED_WATERMARKING_QUOTA) {
      selectedAddon = eligibleAddons.find(
        a => a.entitlement_value === UNLIMITED_CHARGEBEE_ENTITLEMENT_VALUE,
      )
      if (!selectedAddon) {
        console.error('No unlimited addon found for watermarking')
      }
      selectedAddon.quantity = 1
      // @ts-ignore ChargebeeAddOnDetailed should have `quantity`
      return eligibleAddons.map(a => ({ id: a.id, quantity: a.quantity }))
    } else {
      selectedAddon = eligibleAddons.find(
        a => a.entitlement_value !== UNLIMITED_CHARGEBEE_ENTITLEMENT_VALUE,
      )
      if (!selectedAddon) {
        console.error('No addons found for watermarking which is not unlimited')
      }
    }
  } else {
    if (eligibleAddons.length > 1) {
      const entitlementValue = allocated_quantity < 1000 ? 250 : 1000
      selectedAddon = eligibleAddons.find(
        e => e.entitlement_value === entitlementValue.toString(),
      )
    } else {
      selectedAddon = eligibleAddons[0]
    }
  }
  const entitlementValue = selectedAddon.entitlement_value
  selectedAddon.quantity = allocated_quantity / entitlementValue
  if (!Number.isInteger(selectedAddon.quantity)) {
    console.error(
      `Allocated quantity ${allocated_quantity} is not evenly divisible by addon entitlement value ${entitlementValue}`,
    )
  }
  // @ts-ignore ChargebeeAddOnDetailed should have `quantity`
  return eligibleAddons.map(a => ({ id: a.id, quantity: a.quantity }))
}

export const getChargebeeConfig = () => {
  const env = getEnvName()
  if (env === 'prod') {
    return {
      site: 'discoac',
      publishableKey: 'live_rQNw9hyVn1fZ3nPKVvzjcgZF07yzwMin',
    }
  }
  return {
    site: 'discoac-test',
    publishableKey: 'test_236bXhVtXFIQB89TgcuPtzkciYZjm0Xtb',
  }
}

export const getChargebeeInstance = () => {
  if (!window?.Chargebee?.inited) {
    throw new Error('getChargebeeInstance called without Chargebee initialized')
  }

  return window.Chargebee.getInstance()
}

export const redirectToChargebeeCancelPage = async (user: User) => {
  const chargebee = getChargebeeInstance()
  const cancelPage = await chargebee.cancelPage()

  const options = {
    subscription: {
      id: user.business.chargebee_subscription_id,
    },
    firstName: user.first_name,
    lastName: user.last_name,
    saveReturnUrl: `${window.location.protocol}//${window.location.host}/settings/billing`,
    cancelConfirmationUrl: `${window.location.protocol}//${window.location.host}/settings/billing`,
    custom: {
      marketSegment: user.business.type_v2,
    },
  }

  const result = await cancelPage.getPage(options)

  window.location.assign(result.url)
}
